/**
 * SVG path helpers for workflow connector lines (rounded elbows via L + Q).
 */

const DEFAULT_RADIUS = 20;

/** Subtle overlap so separate path endpoints visually merge without gaps. */
export const CONNECTOR_JUNCTION_OVERLAP = 2;

/** Tiny junction cover used only when paths still need a seamless join. */
export const CONNECTOR_JOIN_COVER_RADIUS = 2;

export function buildConnectorJoinCover({
  x,
  y,
  r = CONNECTOR_JOIN_COVER_RADIUS,
}) {
  return { x, y, r };
}

function withOverlap(value, direction, amount = CONNECTOR_JUNCTION_OVERLAP) {
  if (direction === 'down' || direction === 'right') {
    return value + amount;
  }
  return value - amount;
}

function clampRadius(radius, fromX, mergeX, mergeY, fromY) {
  const horizontalSpan = Math.abs(mergeX - fromX);
  const verticalSpan = Math.max(0, mergeY - fromY);
  if (!horizontalSpan || !verticalSpan) return 0;
  return Math.min(radius, horizontalSpan / 2, verticalSpan, 24);
}

/**
 * Build a rounded-elbow path from a card bottom center into the shared merge line.
 *
 * @param {object} params
 * @param {number} params.fromX - Card bottom-center X
 * @param {number} [params.fromY=0] - Card bottom Y in SVG space
 * @param {number} params.mergeX - Shared vertical connector X
 * @param {number} params.mergeY - Horizontal merge line Y
 * @param {number} params.dropY - End Y (or plus top when plusCenterY omitted)
 * @param {number} [params.plusCenterY] - When set, path ends at plusCenterY - plusRadius
 * @param {number} [params.plusRadius=0]
 * @param {number} [params.radius=20]
 * @param {'left'|'right'|'center'|'auto'} [params.role='auto']
 */
export function buildRoundedElbowPath({
  fromX,
  fromY = 0,
  mergeX,
  mergeY,
  dropY,
  plusCenterY = null,
  plusRadius = 0,
  radius = DEFAULT_RADIUS,
  role = 'auto',
}) {
  const endY = plusCenterY != null ? plusCenterY - plusRadius : dropY;
  const resolvedRole = role === 'auto'
    ? (Math.abs(fromX - mergeX) < 1 ? 'center' : (fromX < mergeX ? 'left' : 'right'))
    : role;

  if (resolvedRole === 'center') {
    return `M ${fromX} ${fromY} L ${mergeX} ${endY}`;
  }

  const r = clampRadius(radius, fromX, mergeX, mergeY, fromY);
  if (!r) {
    return `M ${fromX} ${fromY} L ${mergeX} ${endY}`;
  }

  if (resolvedRole === 'left') {
    return [
      `M ${fromX} ${fromY}`,
      `L ${fromX} ${mergeY - r}`,
      `Q ${fromX} ${mergeY}, ${fromX + r} ${mergeY}`,
      `L ${mergeX - r} ${mergeY}`,
      `Q ${mergeX} ${mergeY}, ${mergeX} ${mergeY + r}`,
      `L ${mergeX} ${endY}`,
    ].join(' ');
  }

  return [
    `M ${fromX} ${fromY}`,
    `L ${fromX} ${mergeY - r}`,
    `Q ${fromX} ${mergeY}, ${fromX - r} ${mergeY}`,
    `L ${mergeX + r} ${mergeY}`,
    `Q ${mergeX} ${mergeY}, ${mergeX} ${mergeY + r}`,
    `L ${mergeX} ${endY}`,
  ].join(' ');
}

export function buildVerticalConnectorSegment({
  mergeX,
  fromY,
  toY,
  extendStart = 0,
  extendEnd = 0,
}) {
  const startY = extendStart ? withOverlap(fromY, 'up', extendStart) : fromY;
  const endY = extendEnd ? withOverlap(toY, 'down', extendEnd) : toY;
  return `M ${mergeX} ${startY} L ${mergeX} ${endY}`;
}

/**
 * Build a single continuous branch from a card bottom center to the shared merge point.
 * The path ends exactly at mergeX, mergeY so the trunk can continue without gaps.
 */
export function buildBranchToMergePath({
  fromX,
  fromY = 0,
  mergeX,
  mergeY,
  radius = DEFAULT_RADIUS,
}) {
  if (Math.abs(fromX - mergeX) < 1) {
    return `M ${fromX} ${fromY} L ${mergeX} ${mergeY}`;
  }

  const horizontalSpan = Math.abs(mergeX - fromX);
  const verticalSpan = Math.max(0, mergeY - fromY);
  const r = Math.min(radius, horizontalSpan / 2, verticalSpan);

  if (!r) {
    return `M ${fromX} ${fromY} L ${fromX} ${mergeY} L ${mergeX} ${mergeY}`;
  }

  const goingRight = mergeX > fromX;

  if (goingRight) {
    return [
      `M ${fromX} ${fromY}`,
      `L ${fromX} ${mergeY - r}`,
      `Q ${fromX} ${mergeY} ${fromX + r} ${mergeY}`,
      `L ${mergeX} ${mergeY}`,
    ].join(' ');
  }

  return [
    `M ${fromX} ${fromY}`,
    `L ${fromX} ${mergeY - r}`,
    `Q ${fromX} ${mergeY} ${fromX - r} ${mergeY}`,
    `L ${mergeX} ${mergeY}`,
  ].join(' ');
}

/**
 * Shared trunk from the branch merge point down into the workflow lane.
 * Starts exactly at trunkX, trunkY with no gap after branch paths.
 */
export function buildMergeTrunkPath({
  trunkX,
  trunkY,
  dropY,
  laneX = null,
  radius = DEFAULT_RADIUS,
}) {
  const lane = laneX ?? trunkX;

  if (Math.abs(trunkX - lane) < 1) {
    return `M ${trunkX} ${trunkY} L ${trunkX} ${dropY}`;
  }

  return buildTrunkToLanePath({
    trunkX,
    laneX: lane,
    trunkY,
    dropY,
    radius,
  });
}

/**
 * Drop vertically from a card, then use a short rounded elbow into the main lane.
 */
export function buildStemElbowToLanePath({
  fromX,
  laneX,
  fromY = 0,
  stubY = 22,
  mergeY = 38,
  dropY = 52,
  radius = DEFAULT_RADIUS,
}) {
  if (Math.abs(fromX - laneX) < 1) {
    return buildVerticalConnectorSegment({ mergeX: laneX, fromY, toY: dropY });
  }

  const elbow = buildRoundedElbowPath({
    fromX,
    fromY: stubY,
    mergeX: laneX,
    mergeY,
    dropY,
    radius,
    role: fromX < laneX ? 'left' : 'right',
  });
  const elbowBody = elbow.replace(/^M [\d.]+ [\d.]+ /, '');
  return `M ${fromX} ${fromY} L ${fromX} ${stubY} ${elbowBody}`;
}

/**
 * Connect a merged trigger trunk into the stable main lane with a short elbow.
 */
export function buildTrunkToLanePath({
  trunkX,
  laneX,
  trunkY = 38,
  dropY = 52,
  radius = DEFAULT_RADIUS,
}) {
  if (Math.abs(trunkX - laneX) < 1) {
    return buildVerticalConnectorSegment({ mergeX: laneX, fromY: trunkY, toY: dropY });
  }

  return buildRoundedElbowPath({
    fromX: trunkX,
    fromY: trunkY,
    mergeX: laneX,
    mergeY: trunkY + (dropY - trunkY) / 2,
    dropY,
    radius,
    role: trunkX < laneX ? 'left' : 'right',
  });
}

export function getConnectorPathRole(index, count, cardCenterX, mergeX) {
  if (count <= 1) return 'center';
  if (Math.abs(cardCenterX - mergeX) < 1) return 'center';
  return cardCenterX < mergeX ? 'left' : 'right';
}

const CONDITION_SPLIT_Y = 32;
const CONDITION_CHILD_TOP_Y = 64;

/**
 * Parent vertical stub from condition card bottom to the shared split line.
 */
export function buildConditionParentStemPath({
  parentCenterX,
  parentBottomY = 0,
  splitY = CONDITION_SPLIT_Y,
}) {
  return buildVerticalConnectorSegment({
    mergeX: parentCenterX,
    fromY: parentBottomY,
    toY: splitY,
  });
}

/**
 * Curved fork from the shared split point down into a branch lane card top.
 */
export function buildConditionBranchForkPath({
  parentCenterX,
  childCenterX,
  splitY = CONDITION_SPLIT_Y,
  childTopY = CONDITION_CHILD_TOP_Y,
  radius = DEFAULT_RADIUS,
}) {
  if (Math.abs(parentCenterX - childCenterX) < 1) {
    return buildVerticalConnectorSegment({
      mergeX: parentCenterX,
      fromY: splitY,
      toY: childTopY,
    });
  }

  const horizontalSpan = Math.abs(childCenterX - parentCenterX);
  const verticalSpan = Math.max(0, childTopY - splitY);
  const r = Math.min(radius, horizontalSpan / 2, verticalSpan);

  if (!r) {
    return [
      `M ${parentCenterX} ${splitY}`,
      `L ${parentCenterX} ${childTopY}`,
      `L ${childCenterX} ${childTopY}`,
    ].join(' ');
  }

  const goingRight = childCenterX > parentCenterX;
  const midY = splitY + r;

  if (goingRight) {
    return [
      `M ${parentCenterX} ${splitY}`,
      `L ${parentCenterX} ${midY - r}`,
      `Q ${parentCenterX} ${midY} ${parentCenterX + r} ${midY}`,
      `L ${childCenterX - r} ${midY}`,
      `Q ${childCenterX} ${midY} ${childCenterX} ${midY + r}`,
      `L ${childCenterX} ${childTopY}`,
    ].join(' ');
  }

  return [
    `M ${parentCenterX} ${splitY}`,
    `L ${parentCenterX} ${midY - r}`,
    `Q ${parentCenterX} ${midY} ${parentCenterX - r} ${midY}`,
    `L ${childCenterX + r} ${midY}`,
    `Q ${childCenterX} ${midY} ${childCenterX} ${midY + r}`,
    `L ${childCenterX} ${childTopY}`,
  ].join(' ');
}

/**
 * Build fork paths for all branch lanes under a condition node.
 */
export function buildConditionBranchForkPaths({
  parentCenterX,
  childCenterXs,
  splitY = CONDITION_SPLIT_Y,
  childTopY = CONDITION_CHILD_TOP_Y,
  radius = DEFAULT_RADIUS,
}) {
  const centers = childCenterXs || [];
  const paths = [
    buildConditionParentStemPath({ parentCenterX, splitY }),
  ];

  centers.forEach((childCenterX) => {
    paths.push(buildConditionBranchForkPath({
      parentCenterX,
      childCenterX,
      splitY,
      childTopY,
      radius,
    }));
  });

  return paths;
}

export const CONDITION_BRANCH_CONNECTOR = {
  splitY: CONDITION_SPLIT_Y,
  childTopY: CONDITION_CHILD_TOP_Y,
};

/**
 * Schema fork: horizontal rail above branch cards, then rounded vertical drop into card top.
 * All coordinates are content-absolute.
 */
export function buildRailToBranchDropPath({
  railStartX,
  childCenterX,
  railY,
  childTopY,
  radius = DEFAULT_RADIUS,
}) {
  if (Math.abs(railStartX - childCenterX) < 1) {
    return buildVerticalConnectorSegment({
      mergeX: childCenterX,
      fromY: railY,
      toY: childTopY,
    });
  }

  const horizontalSpan = Math.abs(childCenterX - railStartX);
  const verticalDrop = Math.max(0, childTopY - railY);
  const r = Math.min(radius, horizontalSpan / 2, verticalDrop);
  const goingRight = childCenterX > railStartX;

  if (!r) {
    return [
      `M ${railStartX} ${railY}`,
      `L ${childCenterX} ${railY}`,
      `L ${childCenterX} ${childTopY}`,
    ].join(' ');
  }

  if (goingRight) {
    return [
      `M ${railStartX} ${railY}`,
      `L ${childCenterX - r} ${railY}`,
      `Q ${childCenterX} ${railY} ${childCenterX} ${railY + r}`,
      `L ${childCenterX} ${childTopY}`,
    ].join(' ');
  }

  return [
    `M ${railStartX} ${railY}`,
    `L ${childCenterX + r} ${railY}`,
    `Q ${childCenterX} ${railY} ${childCenterX} ${railY + r}`,
    `L ${childCenterX} ${childTopY}`,
  ].join(' ');
}

/** @deprecated Use buildRailToBranchDropPath */
export function buildSchemaBranchForkPath(params) {
  const {
    parentCenterX,
    railY,
    childCenterX,
    childTopY,
    radius,
  } = params;

  return buildRailToBranchDropPath({
    railStartX: parentCenterX,
    childCenterX,
    railY,
    childTopY,
    radius,
  });
}

/**
 * Rounded curve from the left merge rail into the center trunk (mirrored quarter-turn).
 */
export function buildMirroredCenterMergeLeftCurve({
  centerX,
  mergeY,
  radius = 14,
}) {
  return `M ${centerX - radius} ${mergeY} Q ${centerX} ${mergeY} ${centerX} ${mergeY + radius}`;
}

export function buildMirroredCenterMergeRightCurve({
  centerX,
  mergeY,
  radius = 14,
}) {
  return `M ${centerX + radius} ${mergeY} Q ${centerX} ${mergeY} ${centerX} ${mergeY + radius}`;
}

/**
 * Left center curve and trunk as one continuous path.
 */
export function buildContinuousTriggerCenterMergePath({
  centerX,
  mergeY,
  trunkBottomY,
  trunkEndX = centerX,
  radius = 14,
  overlap = CONNECTOR_JUNCTION_OVERLAP,
}) {
  const apexY = mergeY + radius;
  if (Math.abs(centerX - trunkEndX) < 1) {
    return [
      `M ${centerX - radius} ${mergeY}`,
      `Q ${centerX} ${mergeY} ${centerX} ${apexY}`,
      `L ${centerX} ${withOverlap(trunkBottomY, 'down', overlap)}`,
    ].join(' ');
  }

  const bridgeY = mergeY + Math.max(radius * 2, (trunkBottomY - mergeY) * 0.5);
  const direction = trunkEndX > centerX ? 1 : -1;

  return [
    `M ${centerX - radius} ${mergeY}`,
    `Q ${centerX} ${mergeY} ${centerX} ${apexY}`,
    `L ${centerX} ${bridgeY - radius}`,
    `Q ${centerX} ${bridgeY} ${centerX + direction * radius} ${bridgeY}`,
    `L ${trunkEndX - direction * radius} ${bridgeY}`,
    `Q ${trunkEndX} ${bridgeY} ${trunkEndX} ${bridgeY + radius}`,
    `L ${trunkEndX} ${withOverlap(trunkBottomY, 'down', overlap)}`,
  ].join(' ');
}

/**
 * Rounded curve from the condition stem into the left branch rail.
 */
export function buildConditionSplitLeftCurve({
  parentCenterX,
  railY,
  centerRadius = 14,
}) {
  return [
    `M ${parentCenterX} ${railY - centerRadius}`,
    `Q ${parentCenterX} ${railY} ${parentCenterX - centerRadius} ${railY}`,
  ].join(' ');
}

export function buildConditionSplitRightCurve({
  parentCenterX,
  railY,
  centerRadius = 14,
}) {
  return [
    `M ${parentCenterX} ${railY - centerRadius}`,
    `Q ${parentCenterX} ${railY} ${parentCenterX + centerRadius} ${railY}`,
  ].join(' ');
}

/**
 * Orthogonal trigger branch: vertical stem, rounded 90° elbow, horizontal rail toward merge center.
 * Stops before the center notch so left/right rails do not form a square corner at the trunk.
 */
export function buildOrthogonalTriggerBranchPath({
  itemCenterX,
  itemBottomY,
  mergeX,
  mergeY,
  radius = 14,
  centerRadius = 14,
}) {
  const isLeft = itemCenterX < mergeX - 1;
  const isRight = itemCenterX > mergeX + 1;

  if (!isLeft && !isRight) {
    return buildVerticalConnectorSegment({
      mergeX: itemCenterX,
      fromY: itemBottomY,
      toY: mergeY + centerRadius,
    });
  }

  const railStopX = isLeft
    ? mergeX - centerRadius
    : mergeX + centerRadius;

  return buildBranchToMergePath({
    fromX: itemCenterX,
    fromY: itemBottomY,
    mergeX: railStopX,
    mergeY,
    radius,
  });
}

/**
 * Symmetric rounded center merge: mirrored left and right curves into trunk.
 * @deprecated Prefer buildMirroredCenterMergeLeftCurve + buildMirroredCenterMergeRightCurve
 */
export function buildRoundedCenterMergeNotchPath({
  centerX,
  mergeY,
  radius = 14,
}) {
  return [
    buildMirroredCenterMergeLeftCurve({ centerX, mergeY, radius }),
    buildMirroredCenterMergeRightCurve({ centerX, mergeY, radius }),
  ].join(' ');
}

/**
 * Vertical trunk continuing down from the rounded center merge notch.
 */
export function buildRoundedCenterMergeTrunkPath({
  centerX,
  mergeY,
  trunkBottomY,
  radius = 14,
  overlap = CONNECTOR_JUNCTION_OVERLAP,
}) {
  return buildVerticalConnectorSegment({
    mergeX: centerX,
    fromY: mergeY + radius,
    toY: trunkBottomY,
    extendStart: overlap,
    extendEnd: overlap,
  });
}

/** @deprecated Use buildRoundedCenterMergeTrunkPath */
export function buildOrthogonalMergeTrunkPath(params) {
  return buildRoundedCenterMergeTrunkPath(params);
}

/**
 * Build all trigger-row connector paths with a rounded center merge notch.
 */
export function buildRoundedOrthogonalTriggerMergePaths({
  topRowItemNodes = [],
  visibleRowCenterX,
  mergeY,
  insertPlusNode = null,
  actionLaneCenterX = null,
  radius = 14,
  centerRadius = 14,
}) {
  const paths = [];
  const trunkEndX = actionLaneCenterX ?? insertPlusNode?.centerX ?? visibleRowCenterX;

  if (!insertPlusNode) {
    return paths;
  }

  if (topRowItemNodes.length === 1) {
    const fromX = topRowItemNodes[0].centerX;
    if (Math.abs(fromX - trunkEndX) < 1) {
      paths.push(buildVerticalConnectorSegment({
        mergeX: fromX,
        fromY: topRowItemNodes[0].bottomY,
        toY: insertPlusNode.topY,
      }));
    } else {
      const bridgeY = insertPlusNode.topY * 0.55;
      const direction = trunkEndX > fromX ? 1 : -1;
      paths.push([
        `M ${fromX} ${topRowItemNodes[0].bottomY}`,
        `L ${fromX} ${bridgeY - radius}`,
        `Q ${fromX} ${bridgeY} ${fromX + direction * radius} ${bridgeY}`,
        `L ${trunkEndX - direction * radius} ${bridgeY}`,
        `Q ${trunkEndX} ${bridgeY} ${trunkEndX} ${bridgeY + radius}`,
        `L ${trunkEndX} ${insertPlusNode.topY}`,
      ].join(' '));
    }
    return paths;
  }

  topRowItemNodes.forEach((node) => {
    paths.push(buildOrthogonalTriggerBranchPath({
      itemCenterX: node.centerX,
      itemBottomY: node.bottomY,
      mergeX: visibleRowCenterX,
      mergeY,
      radius,
      centerRadius,
    }));
  });

  paths.push(buildContinuousTriggerCenterMergePath({
    centerX: visibleRowCenterX,
    mergeY,
    trunkBottomY: insertPlusNode.topY,
    trunkEndX,
    radius: centerRadius,
  }));

  paths.push(buildMirroredCenterMergeRightCurve({
    centerX: visibleRowCenterX,
    mergeY,
    radius: centerRadius,
  }));

  return paths;
}

/** Production trigger connector zone geometry (SVG coords: card bottoms at y=0). */
export const PRODUCTION_TRIGGER_CONNECTOR = {
  mergeY: 38,
  insertPlusTopY: 52,
  radius: 16,
  centerRadius: 16,
};

/**
 * Build labeled trigger merge path entries for the production workflow builder.
 * Uses the same rounded orthogonal merge as the proof page.
 */
export function buildProductionTriggerMergePathEntries({
  triggerCenters = [],
  addTileCenterX = null,
  includeAddTileConnector = false,
  visibleRowCenterX,
  actionLaneCenterX = null,
  mergeY = PRODUCTION_TRIGGER_CONNECTOR.mergeY,
  insertPlusTopY = PRODUCTION_TRIGGER_CONNECTOR.insertPlusTopY,
  radius = PRODUCTION_TRIGGER_CONNECTOR.radius,
  centerRadius = PRODUCTION_TRIGGER_CONNECTOR.centerRadius,
}) {
  const mergeSources = [
    ...triggerCenters,
    ...(includeAddTileConnector && Number.isFinite(addTileCenterX) ? [addTileCenterX] : []),
  ];

  if (!mergeSources.length || visibleRowCenterX == null) {
    return [];
  }

  const trunkEndX = actionLaneCenterX ?? visibleRowCenterX;
  const topRowItemNodes = mergeSources.map((centerX) => ({
    centerX,
    bottomY: 0,
  }));

  const pathStrings = buildRoundedOrthogonalTriggerMergePaths({
    topRowItemNodes,
    visibleRowCenterX,
    mergeY,
    actionLaneCenterX: trunkEndX,
    insertPlusNode: {
      centerX: trunkEndX,
      topY: insertPlusTopY,
    },
    radius,
    centerRadius,
  });

  if (topRowItemNodes.length === 1) {
    return [{
      d: pathStrings[0],
      pathType: 'single',
      centerX: topRowItemNodes[0].centerX,
    }];
  }

  const entries = topRowItemNodes.map((node, index) => ({
    d: pathStrings[index],
    pathType: index >= triggerCenters.length ? 'add-trigger-branch' : 'trigger-branch',
    centerX: node.centerX,
  }));

  if (pathStrings.length > topRowItemNodes.length) {
    entries.push({
      d: pathStrings[topRowItemNodes.length],
      pathType: 'merge-trunk',
      centerX: visibleRowCenterX,
    });
  }

  if (pathStrings.length > topRowItemNodes.length + 1) {
    entries.push({
      d: pathStrings[topRowItemNodes.length + 1],
      pathType: 'merge-trunk-right',
      centerX: visibleRowCenterX,
    });
  }

  return entries;
}

/** Production condition branch fork geometry (matches proof-page schema). */
export const PRODUCTION_CONDITION_BRANCH = {
  childTopY: 64,
  branchRailGapAboveCard: 32,
  cornerRadius: 14,
  centerRadius: 14,
};

/**
 * Build labeled condition branch path entries for the production workflow builder.
 * Uses the same fork/rail geometry as the proof page.
 */
export function buildProductionConditionBranchPathEntries({
  parentCenterX,
  childCenterXs = [],
  childTopY = PRODUCTION_CONDITION_BRANCH.childTopY,
  branchRailGapAboveCard = PRODUCTION_CONDITION_BRANCH.branchRailGapAboveCard,
  radius = PRODUCTION_CONDITION_BRANCH.cornerRadius,
  centerRadius = PRODUCTION_CONDITION_BRANCH.centerRadius,
}) {
  if (!childCenterXs.length || parentCenterX == null) {
    return [];
  }

  const branchRailY = childTopY - branchRailGapAboveCard;
  const branchNodes = childCenterXs.map((centerX) => ({
    centerX,
    topY: childTopY,
  }));

  const pathStrings = buildSchemaConditionBranchPaths({
    conditionNode: { centerX: parentCenterX, bottomY: 0 },
    branchNodes,
    branchRailY,
    radius,
    centerRadius,
  });

  if (!pathStrings.length) {
    return [];
  }

  const entries = [{
    d: pathStrings[0],
    pathType: 'condition-parent-stem',
    centerX: parentCenterX,
  }];

  pathStrings.slice(1).forEach((d, index) => {
    entries.push({
      d,
      pathType: 'condition-branch-fork',
      centerX: parentCenterX,
      branchIndex: index,
    });
  });

  return entries;
}

/**
 * Junction cover points for trigger merge center (seamless join helpers).
 */
export function getTriggerMergeJunctionCovers({
  centerX,
  mergeY,
  radius = 14,
}) {
  return [
    buildConnectorJoinCover({ x: centerX, y: mergeY }),
    buildConnectorJoinCover({ x: centerX, y: mergeY + radius }),
    buildConnectorJoinCover({ x: centerX - radius, y: mergeY }),
    buildConnectorJoinCover({ x: centerX + radius, y: mergeY }),
  ];
}

/** @deprecated Use buildRoundedOrthogonalTriggerMergePaths */
export function buildSchemaTriggerConnectorPaths(params) {
  return buildRoundedOrthogonalTriggerMergePaths(params);
}

/**
 * Downstream lane from insert plus to the next node (action, condition, or END).
 */
export function buildSchemaInsertPlusToNextPath({
  insertPlusNode,
  nextNode,
  laneCenterX = null,
  overlap = CONNECTOR_JUNCTION_OVERLAP,
}) {
  const mergeX = laneCenterX ?? insertPlusNode.centerX;
  return buildVerticalConnectorSegment({
    mergeX,
    fromY: insertPlusNode.bottomY,
    toY: nextNode.topY,
    extendStart: overlap,
    extendEnd: overlap,
  });
}

/**
 * Vertical link between two stacked nodes (action -> condition, branch -> plus, etc).
 */
export function buildSchemaNodeLinkPath({ fromNode, toNode, overlap = CONNECTOR_JUNCTION_OVERLAP }) {
  return buildVerticalConnectorSegment({
    mergeX: fromNode.centerX,
    fromY: fromNode.bottomY,
    toY: toNode.topY,
    extendStart: overlap,
    extendEnd: overlap,
  });
}

/**
 * Condition stem + symmetric branch forks in content coordinates.
 */
export function buildSchemaConditionBranchPaths({
  conditionNode,
  branchNodes,
  branchRailY,
  radius = DEFAULT_RADIUS,
  centerRadius = 14,
  overlap = CONNECTOR_JUNCTION_OVERLAP,
}) {
  const parentX = conditionNode.centerX;
  const paths = [];

  const leftBranches = branchNodes.filter((node) => node.centerX < parentX - 1);
  const centerBranches = branchNodes.filter((node) => Math.abs(node.centerX - parentX) < 1);
  const rightBranches = branchNodes.filter((node) => node.centerX > parentX + 1);
  const junctionY = branchRailY - centerRadius;

  if (centerBranches.length > 0) {
    paths.push(buildVerticalConnectorSegment({
      mergeX: parentX,
      fromY: conditionNode.bottomY,
      toY: centerBranches[0].topY,
      extendEnd: overlap,
    }));
  } else {
    paths.push(buildVerticalConnectorSegment({
      mergeX: parentX,
      fromY: conditionNode.bottomY,
      toY: junctionY,
    }));
  }

  if (leftBranches.length > 0) {
    paths.push(buildConditionSplitLeftCurve({
      parentCenterX: parentX,
      railY: branchRailY,
      centerRadius,
    }));

    leftBranches.forEach((branchNode) => {
      paths.push(buildRailToBranchDropPath({
        railStartX: parentX - centerRadius,
        childCenterX: branchNode.centerX,
        railY: branchRailY,
        childTopY: branchNode.topY,
        radius,
      }));
    });
  }

  if (rightBranches.length > 0) {
    paths.push(buildConditionSplitRightCurve({
      parentCenterX: parentX,
      railY: branchRailY,
      centerRadius,
    }));

    rightBranches.forEach((branchNode) => {
      paths.push(buildRailToBranchDropPath({
        railStartX: parentX + centerRadius,
        childCenterX: branchNode.centerX,
        railY: branchRailY,
        childTopY: branchNode.topY,
        radius,
      }));
    });
  }

  return paths;
}

/**
 * Junction cover points for condition branch center split.
 */
export function getConditionBranchJunctionCovers({
  parentCenterX,
  railY,
  centerRadius = 14,
}) {
  return [
    buildConnectorJoinCover({ x: parentCenterX, y: railY }),
    buildConnectorJoinCover({ x: parentCenterX, y: railY - centerRadius }),
    buildConnectorJoinCover({ x: parentCenterX - centerRadius, y: railY }),
    buildConnectorJoinCover({ x: parentCenterX + centerRadius, y: railY }),
  ];
}

/**
 * Branch lane: card bottom -> plus top, plus bottom -> end top.
 */
export function buildSchemaBranchLanePaths({
  branchNode,
  plusNode,
  endNode,
  overlap = CONNECTOR_JUNCTION_OVERLAP,
}) {
  return [
    buildVerticalConnectorSegment({
      mergeX: branchNode.centerX,
      fromY: branchNode.bottomY,
      toY: plusNode.topY,
      extendEnd: overlap,
    }),
    buildVerticalConnectorSegment({
      mergeX: branchNode.centerX,
      fromY: plusNode.bottomY,
      toY: endNode.topY,
      extendStart: overlap,
      extendEnd: overlap,
    }),
  ];
}

/**
 * Build proof-page connector render data with seamless junction covers.
 */
export function buildWorkflowProofConnectorRenderData({
  paths = [],
  junctionCovers = [],
}) {
  return { paths, junctionCovers };
}
